/**
 * In-memory mock backend.
 *
 * Mirrors the NestJS API surface so the app is fully usable before the
 * real backend exists. Every exported function returns the same shape the
 * live endpoints will return.
 */

import type {
    AuthSession,
    ChatMessage,
    ChatInbox,
    ChatSessionPage,
    ChatSession,
    AdminClaim,
    CreateSmallGroupInput,
    Cue,
    CueCounts,
    GrowthSummary,
    OwnerSpot,
    PublicSpot,
    Reflection,
    ReflectionExport,
    ReflectionExportOptions,
    ReflectionHistoryQuery,
    ReflectionInput,
    ReflectionPage,
    ReflectionQuota,
    ReportInput,
    SupportInput,
    SupportSubmitResult,
    SmallGroup,
    TrendPoint,
    SmallGroupInbox,
    SmallGroupInboxSections,
    SmallGroupInvite,
    SmallGroupMessage,
    SocialGoal,
    GuestProfile,
    InviteView,
    BlockedUser,
    NearbyResult,
    NearbyUser,
    PhotoExchangeState,
    ByoIntakeResult,
    PracticeCredits,
    PracticeMessage,
    ProgressDashboard,
    PracticePack,
    PracticeScenario,
    PracticeSendResult,
    PracticeSession,
    PracticeSessionView,
    PracticeSummary,
    PracticeSummaryResponse,
    RevealResult,
    User,
    UserAvatarConfig,
    Venue,
    MoodCatalog,
    MoodContextCatalogItem,
    MoodCatalogItem,
    MoodJarEntry,
    MoodJarSummary,
    MonthlyVibeCard,
    MonthlyVibeCardStatusResponse,
} from "@/types";
import { normalizeAvatarConfig } from "@/lib/avatar-config";
import { NEARBY_RADIUS_DEFAULT_M } from "@/lib/nearby-radius";
import { activeGoalLimit } from "@/lib/goal-limits";
import { reflectionLimits } from "@/lib/reflection-limits";
import { MOCK_LATENCY } from "./config";
import {
  AGE_BAND_MIN_FLOOR,
  OPEN_AGE_BAND_FLOOR,
  ageBandRangeLabel,
} from "@/lib/age-bands";

export const delay = <T>(value: T, ms = MOCK_LATENCY): Promise<T> =>
  new Promise((resolve) => setTimeout(() => resolve(value), ms));

export const uid = () => Math.random().toString(36).slice(2, 10);

/**
 * Photo exchange state, per conversation, held in memory.
 *
 * The mock tracks attempts as a plain count rather than mirroring the server's
 * attempt rows — enough to exercise every card state (can ask / waiting /
 * incoming / revealed / out of attempts) without duplicating rules that already
 * have their own tests on the backend.
 */
const photoExchanges = new Map<
  string,
  PhotoExchangeState & { myAttemptsUsed: number }
>();

function photoExchangeFor(chatId: string) {
  const existing = photoExchanges.get(chatId);
  if (existing) return existing;
  const fresh = {
    status: "available" as const,
    attemptsLeft: 2,
    pending: null,
    revealed: null,
    canRequest: true,
    myAttemptsUsed: 0,
  };
  photoExchanges.set(chatId, fresh);
  return fresh;
}

function closeMockAttempt(chatId: string) {
  const state = photoExchangeFor(chatId);
  if (!state.pending) throw new Error("Nothing to close");
  if (state.pending.byMe) {
    state.myAttemptsUsed += 1;
    state.attemptsLeft = Math.max(0, 2 - state.myAttemptsUsed);
  }
  state.pending = null;
  state.canRequest = state.attemptsLeft > 0;
  if (state.attemptsLeft === 0) state.status = "exhausted";
  return state;
}

const CHAT_INACTIVE_MS = 14 * 24 * 60 * 60 * 1000;
const CHAT_INACTIVE_WARNING_MS = 13 * 24 * 60 * 60 * 1000;

function chatInactiveAtFromNow() {
  return new Date(Date.now() + CHAT_INACTIVE_MS).toISOString();
}

/**
 * Close the profile on a blocked conversation, leaving the person identifiable.
 *
 * The mock's stand-in for the server reading the `blocks` table: here a chat
 * offering Unblock is by definition one where a block stands. Applied on the
 * way out rather than stored, so lifting the block gives the profile back
 * without the fixture having to remember what was in it.
 *
 * The NAME and avatar stay. This used to blank them too, matching a server that
 * did the same — and the blocker was left with a column of identical "Someone"
 * cards and no way to tell which thread held the person they meant to unblock.
 * Only a deleted account reads "Someone" now, where it is the truth rather than
 * a redaction.
 */
function withPeerVisibility(chat: ChatSession): ChatSession {
  if (!chat.canUnblock) return { ...chat, peerHidden: false };
  return {
    ...chat,
    peerHidden: true,
    peerAvatarConfig: null,
    peerVenueTag: null,
    peerAgeBand: null,
    peerHeadline: null,
    peerAbout: null,
    peerInterests: null,
    peerLookingFor: null,
    peerLanguages: null,
  };
}

function withChatLifecycleState(input: ChatSession): ChatSession {
  const chat = withPeerVisibility(input);
  const reveal = reveals[chat.id];
  const revealed = chat.revealed || Boolean(reveal?.a && reveal?.b);
  const revealStatus = revealed
    ? "revealed"
    : reveal?.a
      ? "requested"
      : reveal?.b
        ? "pending_peer"
        : "idle";
  if (!chat.inactiveAt) return chat;
  const deadline = Date.parse(chat.inactiveAt);
  const now = Date.now();
  return {
    ...chat,
    revealed,
    revealStatus,
    revealRequestedByMe: Boolean(reveal?.a),
    revealRequestedByPeer: Boolean(reveal?.b),
    inactiveWarning:
      chat.state === "active" &&
      now >= deadline - (CHAT_INACTIVE_MS - CHAT_INACTIVE_WARNING_MS) &&
      now < deadline,
  };
}

let me: User | null = null;

/** Client mirror of the backend mood catalog (subset is fine for dev). */
const MOCK_MOOD_CATALOG: MoodCatalogItem[] = [
  { id: "calm", emoji: "🌊", valence: 1, energy: "low", social: "guarded", gentle: false },
  { id: "centered", emoji: "◎", valence: 1, energy: "low", social: "guarded", gentle: false },
  { id: "content", emoji: "🙂", valence: 1, energy: "low", social: "guarded", gentle: false },
  { id: "peaceful", emoji: "🕊️", valence: 1, energy: "low", social: "guarded", gentle: false },
  { id: "relaxed", emoji: "🍃", valence: 1, energy: "low", social: "guarded", gentle: false },
  { id: "present", emoji: "◌", valence: 1, energy: "low", social: "open", gentle: false },
  { id: "happy", emoji: "😄", valence: 2, energy: "medium", social: "open", gentle: false },
  { id: "delighted", emoji: "✨", valence: 2, energy: "medium", social: "open", gentle: false },
  { id: "excited", emoji: "🎉", valence: 2, energy: "high", social: "high", gentle: false },
  { id: "energized", emoji: "⚡", valence: 2, energy: "high", social: "high", gentle: false },
  { id: "inspired", emoji: "💡", valence: 2, energy: "medium", social: "open", gentle: false },
  { id: "playful", emoji: "🎈", valence: 2, energy: "medium", social: "open", gentle: false },
  { id: "refreshed", emoji: "🌤️", valence: 2, energy: "medium", social: "open", gentle: false },
  { id: "grateful", emoji: "🙏", valence: 2, energy: "medium", social: "open", gentle: false },
  { id: "hopeful", emoji: "🌱", valence: 1, energy: "medium", social: "open", gentle: false },
  { id: "proud", emoji: "🌟", valence: 2, energy: "medium", social: "open", gentle: false },
  { id: "confident", emoji: "💪", valence: 2, energy: "medium", social: "open", gentle: false },
  { id: "curious", emoji: "🔎", valence: 1, energy: "medium", social: "open", gentle: false },
  { id: "connected", emoji: "🤝", valence: 2, energy: "medium", social: "high", gentle: false },
  { id: "caring", emoji: "💛", valence: 1, energy: "medium", social: "open", gentle: false },
  { id: "vulnerable", emoji: "🫧", valence: 0, energy: "low", social: "guarded", gentle: true },
  { id: "sad", emoji: "💧", valence: -2, energy: "low", social: "low", gentle: true },
  { id: "lonely", emoji: "🌙", valence: -2, energy: "low", social: "low", gentle: true },
  { id: "disappointed", emoji: "☁️", valence: -1, energy: "low", social: "guarded", gentle: false },
  { id: "grieving", emoji: "🕯️", valence: -2, energy: "low", social: "low", gentle: true },
  { id: "weary", emoji: "🪫", valence: -1, energy: "low", social: "low", gentle: false },
  { id: "anxious", emoji: "😰", valence: -1, energy: "medium", social: "guarded", gentle: true },
  { id: "nervous", emoji: "😬", valence: -1, energy: "medium", social: "guarded", gentle: false },
  { id: "worried", emoji: "💭", valence: -1, energy: "medium", social: "guarded", gentle: true },
  { id: "scared", emoji: "🫨", valence: -2, energy: "high", social: "low", gentle: true },
  { id: "overwhelmed", emoji: "🌧️", valence: -2, energy: "high", social: "low", gentle: true },
  { id: "stressed", emoji: "🧩", valence: -1, energy: "high", social: "guarded", gentle: false },
  { id: "burned_out", emoji: "🕳️", valence: -2, energy: "low", social: "low", gentle: true },
  { id: "frustrated", emoji: "😤", valence: -1, energy: "high", social: "guarded", gentle: false },
  { id: "irritated", emoji: "🌵", valence: -1, energy: "medium", social: "guarded", gentle: false },
  { id: "angry", emoji: "🔥", valence: -1, energy: "high", social: "guarded", gentle: false },
  { id: "restless", emoji: "〰️", valence: 0, energy: "high", social: "guarded", gentle: false },
  { id: "confused", emoji: "❔", valence: 0, energy: "medium", social: "guarded", gentle: false },
  { id: "bored", emoji: "…", valence: 0, energy: "low", social: "guarded", gentle: false },
  { id: "numb", emoji: "🫥", valence: -1, energy: "low", social: "low", gentle: true },
  { id: "embarrassed", emoji: "🫣", valence: -1, energy: "medium", social: "guarded", gentle: false },
  { id: "guilty", emoji: "🪨", valence: -1, energy: "low", social: "guarded", gentle: true },
  { id: "ashamed", emoji: "🥀", valence: -2, energy: "low", social: "low", gentle: true },
  { id: "meh", emoji: "😐", valence: 0, energy: "low", social: "guarded", gentle: false },
  { id: "overthinking", emoji: "🌀", valence: -1, energy: "medium", social: "guarded", gentle: true },
  { id: "other", emoji: "✎", valence: 0, energy: "medium", social: "guarded", gentle: false },
];
const MOCK_MOOD_CONTEXTS: MoodContextCatalogItem[] = [
  { id: "work", emoji: "💼" },
  { id: "people", emoji: "👥" },
  { id: "home", emoji: "🏠" },
  { id: "sleep", emoji: "🛌" },
  { id: "health", emoji: "🫶" },
  { id: "plans", emoji: "📅" },
  { id: "weather", emoji: "☁️" },
  { id: "money", emoji: "🪙" },
  { id: "one_of_those_days", emoji: "〰️" },
  { id: "other", emoji: "✎" },
];

/** In-memory Mood Jar + Monthly Vibe Card state for mock/dev (no backend). */
const mockMoodJar: MoodJarEntry[] = [];
const mockMonthlyCards: Record<string, MonthlyVibeCard> = {};

/** Resolve a mock month's card state (available if generated, else ready). */
function mockMonthlyCardState(periodKey: string): MonthlyVibeCard {
  if (mockMonthlyCards[periodKey]) return { ...mockMonthlyCards[periodKey] };
  return {
    periodKey,
    status: "ready",
    cardId: null,
    createdAt: null,
    completedAt: null,
    content: null,
    eligibility: { entryCount: 8, activeDays: 5, minEntries: 5, minActiveDays: 3 },
  };
}

const MOCK_CONCEPTS = [
  "monthly_vibe_verdict",
  "month_in_one_pic",
  "monthly_vibe_receipt",
] as const;

/** Deterministic-ish mock concept so all three renderers can be previewed. */
function mockConcept(periodKey: string): (typeof MOCK_CONCEPTS)[number] {
  const n = [...periodKey].reduce((a, c) => a + c.charCodeAt(0), 0);
  return MOCK_CONCEPTS[n % MOCK_CONCEPTS.length];
}

const venue: Venue = {
  id: "venue_brewhouse",
  name: "Brew House Café",
  category: "Café",
  distanceMeters: 8,
};


/**
 * Two blocks, mutable, so the mock can demonstrate a lift.
 *
 * One from a conversation and one from a declined Cue — the second is the case
 * that makes the list necessary, since it leaves no thread to unblock from.
 */
const blockedUsers: BlockedUser[] = [
  {
    id: "u_greywolf",
    displayName: "Grey Wolf",
    avatarSeed: "GreyWolf",
    blockedAt: new Date(Date.now() - 3 * 24 * 60 * 60 * 1000).toISOString(),
  },
  {
    id: "u_quietheron",
    displayName: "Quiet Heron",
    avatarSeed: "QuietHeron",
    blockedAt: new Date(Date.now() - 40 * 24 * 60 * 60 * 1000).toISOString(),
  },
];

const nearby: NearbyUser[] = [
  {
    id: "u_bluefox",
    displayName: "Blue Fox",
    venueTag: "K7",
    avatarSeed: "BlueFox",
    status: "open",
    vibe: { emoji: "☕️", label: "Caffeinated" },
    ageBand: "25-29",
    headline: "Always looking for good coffee and better conversations",
    about:
      "Usually happiest over a slow coffee, a walk, or a good startup story.",
    interests: ["Coffee", "Startups", "Live music"],
    lookingFor: ["Make New Friends"],
    languages: ["English"],
  },
  {
    id: "u_greenowl",
    displayName: "Green Owl",
    venueTag: "M4",
    avatarSeed: "GreenOwl",
    status: "working",
    vibe: { emoji: "🦉", label: "Heads down" },
    ageBand: "30-34",
    headline: "Deep work now, open to a quick hello after",
    about: "Into quiet spaces, thoughtful chats, and finding good reading spots.",
    interests: ["Books", "Technology", "Coffee"],
    lookingFor: ["Coffee Chats"],
    languages: ["English", "French"],
  },
  {
    id: "u_redpanda",
    displayName: "Red Panda",
    venueTag: "P2",
    avatarSeed: "RedPanda",
    status: "new",
    vibe: { emoji: "👋", label: "New here" },
    ageBand: "18-24",
    headline: "New in town and collecting friendly recommendations",
    about: "Ask me about food markets, films, or places to explore nearby.",
    interests: ["Food", "Movies", "Travel"],
    lookingFor: ["Find Community"],
    languages: ["English"],
  },
  {
    id: "u_coffeeghost",
    displayName: "CoffeeGhost",
    venueTag: "C9",
    avatarSeed: "CoffeeGhost",
    status: "open",
    vibe: { emoji: "📚", label: "Reading" },
    ageBand: "25-29",
    headline: "Reading today, chatting if the conversation is gentle",
    about: "A low-pressure hello is perfect. Books, art, and calm corners help.",
    interests: ["Books", "Art", "Coffee"],
    lookingFor: ["Build Close Friendships"],
    languages: ["English"],
  },
  {
    id: "u_quietwhale",
    displayName: "Quiet Whale",
    venueTag: "Q4",
    avatarSeed: "QuietWhale",
    status: "working",
    vibe: null,
    ageBand: "35-39",
    headline: "Prefer thoughtful chats to small talk",
    about: "Here to work, but open to meeting people who like hiking and music.",
    interests: ["Hiking", "Music", "Technology"],
    lookingFor: ["Find Activity Partners"],
    languages: ["English", "Spanish"],
  },
];

/**
 * Attributes the nearby payload deliberately does not carry.
 *
 * Kept beside the fixtures rather than on `NearbyUser` so the mock discloses
 * them where the real API does — in a reveal, once two people have connected —
 * and nowhere else. Putting them back on the nearby list would make the mock
 * describe a server that broadcasts gender, religion and politics to everyone
 * in range, which is exactly what was removed.
 */
const revealOnlyAttributes: Record<
  string,
  { gender: string; religion: string; politics: string }
> = {
  u_bluefox: { gender: "woman", religion: "spiritual", politics: "moderate" },
  u_greenowl: {
    gender: "man",
    religion: "agnostic",
    politics: "not-political",
  },
  u_redpanda: {
    gender: "non-binary",
    religion: "prefer-not-to-say",
    politics: "independent",
  },
  u_coffeeghost: {
    gender: "prefer-not-to-say",
    religion: "other",
    politics: "prefer-not-to-say",
  },
  u_quietwhale: {
    gender: "another-identity",
    religion: "spiritual",
    politics: "liberal",
  },
};

const cues: Cue[] = [
  {
    id: uid(),
    fromUserId: "u_redpanda",
    toUserId: "me",
    venueId: venue.id,
    status: "pending",
    createdAt: new Date(Date.now() - 1000 * 60 * 3).toISOString(),
    fromName: "Red Panda",
    fromAvatarSeed: "RedPanda",
    fromVenueTag: "R4",
    fromAgeBand: "25-29",
    fromHeadline: "Always looking for good coffee and better conversations",
    fromInterests: ["Coffee", "Startups", "Live music"],
    fromLookingFor: ["Make New Friends"],
  },
];

const chats: ChatSession[] = [];
const messages: ChatMessage[] = [];
const reveals: Record<string, { a: boolean; b: boolean }> = {};
const groupExpiresAt = new Date(Date.now() + 28 * 60_000).toISOString();
const groups: SmallGroup[] = [
  {
    id: "group_coffee",
    name: "Front table coffee",
    vibe: "Grab coffee",
    ageRange: null,
    prompt: "Anyone want to grab a table?",
    maxMembers: 4,
    radiusM: 5000,
    memberCount: 3,
    minutesLeft: 28,
    expiresAt: groupExpiresAt,
    isMember: false,
    hostUserId: "u_bluefox",
    viewerMembershipStatus: "none",
    members: [
      {
        id: "u_bluefox",
        displayName: "Blue Fox",
        avatarSeed: "BlueFox",
        ageBand: "25-29",
        readyToMeet: false,
        isHost: true,
      },
      {
        id: "u_redpanda",
        displayName: "Red Panda",
        avatarSeed: "RedPanda",
        ageBand: "25-29",
        readyToMeet: false,
        isHost: false,
      },
      {
        id: "u_coffeeghost",
        displayName: "CoffeeGhost",
        avatarSeed: "CoffeeGhost",
        ageBand: "30-34",
        readyToMeet: false,
        isHost: false,
      },
    ],
  },
];
// Groups this mock user deleted from their own inbox (per-member hide).
const hiddenGroupIds = new Set<string>();

const groupMessages: SmallGroupMessage[] = [
  {
    id: uid(),
    groupId: "group_coffee",
    senderId: "u_bluefox",
    senderName: "Blue Fox",
    kind: "user",
    content: "I’m near the front tables.",
    timestamp: new Date(Date.now() - 4 * 60_000).toISOString(),
  },
  {
    id: uid(),
    groupId: "group_coffee",
    senderId: null,
    senderName: null,
    kind: "system",
    content: "This Small Group expires soon. Meet in person if it feels right.",
    timestamp: new Date(Date.now() - 2 * 60_000).toISOString(),
  },
];

const mockSpots: OwnerSpot[] = [
  {
    id: "spot_brewhouse",
    slug: "the-brew-house-a1b2c3",
    status: "approved",
    venueId: "venue_brewhouse",
    venueName: "The Brew House",
    venueAddress: "12 Market Street",
    welcomeNote: "Come in, grab a seat, and say hi to someone.",
    meetupSpots: ["By the window", "Near the counter"],
    stats: {
      conversationsStarted: 18,
      groupsFormed: 5,
      meetupMoments: 3,
      becameVisible: 24,
    },
    createdAt: new Date(Date.now() - 6 * 24 * 60 * 60_000).toISOString(),
  },
];

const mockReflections: Reflection[] = [
  {
    id: uid(),
    where: "opncue",
    subject: "a_conversation",
    feeling: "good",
    outcome: "made_connection",
    note: "Nice chat by the window.",
    createdAt: new Date(Date.now() - 26 * 60 * 60_000).toISOString(),
  },
  {
    id: uid(),
    where: "somewhere_else",
    subject: "went_somewhere_social",
    feeling: "hard_but_tried",
    outcome: "practiced",
    note: null,
    createdAt: new Date(Date.now() - 50 * 60 * 60_000).toISOString(),
  },
];

/**
 * The mock cursor is just the last row's id, wrapped so it is as opaque to the
 * caller as the server's is — nothing should ever read a cursor's contents.
 */
function encodeMockCursor(id: string): string {
  return `cursor:${id}`;
}

function decodeMockCursor(cursor: string | undefined): string | null {
  return cursor?.startsWith("cursor:") ? cursor.slice("cursor:".length) : null;
}

/** How many reflections were logged this calendar month — for the quota. */
function reflectionsUsedThisMonth(): number {
  const now = new Date();
  const start = new Date(now.getFullYear(), now.getMonth(), 1);
  return mockReflections.filter(
    (r) => new Date(r.createdAt).getTime() >= start.getTime(),
  ).length;
}

/** How many reflections were logged so far today — for the reminder gate. */
function reflectionsUsedToday(): number {
  const now = new Date();
  const start = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  return mockReflections.filter(
    (r) => new Date(r.createdAt).getTime() >= start.getTime(),
  ).length;
}

/** Date-only ("YYYY-MM-DD") `days` from now (negative = in the past). */
function dueDateFromNow(days: number): string {
  const d = new Date();
  d.setDate(d.getDate() + days);
  return d.toISOString().slice(0, 10);
}

const mockGoals: SocialGoal[] = [
  {
    id: uid(),
    text: "Say hi to one new person this week",
    done: false,
    completedAt: null,
    archived: false,
    archivedAt: null,
    createdAt: new Date(Date.now() - 3 * 24 * 60 * 60_000).toISOString(),
    dueDate: dueDateFromNow(4),
    notes: ["Try a simple opener."],
  },
  {
    id: uid(),
    text: "Invite a coworker to lunch",
    done: false,
    completedAt: null,
    archived: false,
    archivedAt: null,
    createdAt: new Date(Date.now() - 12 * 24 * 60 * 60_000).toISOString(),
    // Past due and still open → counts toward "Expired goals".
    dueDate: dueDateFromNow(-3),
    notes: [],
  },
];

/** Goals still occupying a slot. Archived goals are review-only. */
function activeMockGoals(): SocialGoal[] {
  return mockGoals.filter((g) => !g.archived);
}

/** Mirrors `cleanNotes` in the API's goal.service.ts. */
function cleanMockNotes(notes: string[]): string[] {
  return notes
    .map((note) => note.trim().slice(0, 100))
    .filter(Boolean)
    .slice(0, 5);
}

/** Open goal whose due date (end of that day) has passed. */
function isGoalExpired(g: SocialGoal): boolean {
  if (g.archived || g.done || !g.dueDate) return false;
  return new Date(`${g.dueDate}T23:59:59`).getTime() < Date.now();
}

/**
 * Log of goal completions/removals, used to populate the weekly trend tabs
 * (the real backend derives these from metric events). Seeded with a little
 * recent history so the chart looks alive on first open.
 */
const mockGoalEvents: { type: "completed" | "removed"; at: string }[] = [
  { type: "completed", at: new Date(Date.now() - 2 * 24 * 60 * 60_000).toISOString() },
  { type: "completed", at: new Date(Date.now() - 9 * 24 * 60 * 60_000).toISOString() },
  { type: "removed", at: new Date(Date.now() - 10 * 24 * 60 * 60_000).toISOString() },
  { type: "completed", at: new Date(Date.now() - 16 * 24 * 60 * 60_000).toISOString() },
];

const proximityHints = [
  "near the window",
  "by the counter",
  "at the corner table",
  "near the entrance",
];

function publicPeerFields(peer: NearbyUser | null | undefined, fallbackId: string) {
  return {
    peerId: peer?.id ?? fallbackId,
    peerName: peer?.displayName ?? "Someone",
    peerAvatarSeed: peer?.avatarSeed ?? fallbackId,
    peerVenueTag: peer?.venueTag ?? null,
    peerAgeBand: peer?.ageBand ?? null,
    peerHeadline: peer?.headline ?? null,
    peerAbout: peer?.about ?? null,
    peerInterests: peer?.interests ?? null,
    peerLookingFor: peer?.lookingFor ?? null,
    peerLanguages: peer?.languages ?? null,
    peerGender: revealOnlyAttributes[peer?.id ?? ""]?.gender ?? null,
    peerReligion: revealOnlyAttributes[peer?.id ?? ""]?.religion ?? null,
    peerPolitics: revealOnlyAttributes[peer?.id ?? ""]?.politics ?? null,
  };
}

function mockRevealDetailsFromPeer(peer: {
  ageBand?: string | null;
  about?: string | null;
  interests?: string[] | null;
  gender?: string | null;
  religion?: string | null;
  politics?: string | null;
}): NonNullable<GuestProfile["revealDetails"]> {
  const age = peer.ageBand?.match(/^\d+/)?.[0];
  return {
    age: age ? Number(age) : 28,
    about: peer.about ?? null,
    gender: peer.gender ?? null,
    religion: peer.religion ?? null,
    politics: peer.politics ?? null,
  };
}

function guestProfileFromNearby(user: NearbyUser, revealed = false): GuestProfile {
  return {
    id: user.id,
    displayName: user.displayName,
    venueTag: user.venueTag,
    avatarSeed: user.avatarSeed,
    ageBand: user.ageBand,
    headline: user.headline,
    interests: user.interests,
    lookingFor: user.lookingFor,
    languages: user.languages,
    revealed,
    revealDetails: revealed
      ? mockRevealDetailsFromPeer({
          ...user,
          ...revealOnlyAttributes[user.id],
        })
      : {},
  };
}

function refreshGroupTime(group: SmallGroup): SmallGroup {
  return {
    ...group,
    minutesLeft: Math.max(
      0,
      Math.ceil((new Date(group.expiresAt).getTime() - Date.now()) / 60_000),
    ),
    memberCount: group.members.length,
  };
}

export const mockApi = {
  // ---- Auth ----
  async authStart(email: string) {
    return delay({ ok: true, channel: "email" as const, email });
  },

  async authVerify(email: string): Promise<AuthSession> {
    const userId = "me";
    me = me ?? { id: userId, email, displayName: "", avatarSeed: uid() };
    me.email = email;
    return delay({
      accessToken: `mock.${uid()}`,
      refreshToken: `mock.${uid()}`,
      userId,
    });
  },

  async authRefresh(_refreshToken: string): Promise<AuthSession> {
    const userId = me?.id ?? "me";
    return delay({
      accessToken: `mock.${uid()}`,
      refreshToken: `mock.${uid()}`,
      userId,
    });
  },

  async authSocial(
    provider: "google" | "apple",
    displayName?: string,
  ): Promise<AuthSession> {
    const userId = "me";
    const email = `${provider}.user@example.com`;
    me = me ?? {
      id: userId,
      email,
      displayName: displayName ?? "",
      avatarSeed: uid(),
    };
    me.email = email;
    if (displayName && !me.displayName) me.displayName = displayName;
    return delay({
      accessToken: `mock.${uid()}`,
      refreshToken: `mock.${uid()}`,
      userId,
    });
  },

  // ---- Users ----
  async getMe(): Promise<User> {
    me = me ?? { id: "me", displayName: "", avatarSeed: uid() };
    // From the shared constant, not a literal: a mock that seeds a radius the
    // real bounds no longer allow makes the mock disagree with the server about
    // what a new user starts with.
    me.nearbyRadiusM = me.nearbyRadiusM ?? NEARBY_RADIUS_DEFAULT_M;
    return delay({ ...me });
  },

  async updateMe(patch: Partial<User>): Promise<User> {
    me = {
      ...(me ?? { id: "me", displayName: "", avatarSeed: uid() }),
      ...patch,
    };
    return delay({ ...me });
  },

  /** GET /referrals/me — a stable sample invite view for offline/mock mode. */
  async getInviteView(): Promise<InviteView> {
    return delay({
      code: "SUNSET",
      inviteUrl: "https://opncue.com/i/SUNSET",
      rewardedThisMonth: 0,
      monthlyCap: 1,
      referrals: [
        {
          id: "r_demo_1",
          status: "vested",
          createdAt: new Date(Date.now() - 12 * 864e5).toISOString(),
          qualifiedAt: new Date(Date.now() - 10 * 864e5).toISOString(),
          vestedAt: new Date(Date.now() - 3 * 864e5).toISOString(),
        },
        {
          id: "r_demo_2",
          status: "qualified",
          createdAt: new Date(Date.now() - 4 * 864e5).toISOString(),
          qualifiedAt: new Date(Date.now() - 2 * 864e5).toISOString(),
          vestedAt: null,
        },
        {
          id: "r_demo_3",
          status: "pending",
          createdAt: new Date(Date.now() - 1 * 864e5).toISOString(),
          qualifiedAt: null,
          vestedAt: null,
        },
      ],
    });
  },

  /** POST /referrals/redeem — accept any well-formed code in mock mode. */
  async redeemReferral(_code: string): Promise<{ ok: boolean }> {
    return delay({ ok: true });
  },

  /** PATCH /users/me/avatar — save a customized illustrated avatar config. */
  async updateAvatar(avatarConfig: UserAvatarConfig): Promise<User> {
    me = {
      ...(me ?? { id: "me", displayName: "", avatarSeed: uid() }),
      avatarConfig: normalizeAvatarConfig(avatarConfig),
    };
    return delay({ ...me });
  },

  // ---- Mood Jar (mock, in-memory) ----------------------------------------

  async moodJarCatalog(): Promise<MoodCatalog> {
    return delay({
      moods: MOCK_MOOD_CATALOG.map((m) => ({ ...m })),
      contexts: MOCK_MOOD_CONTEXTS.map((m) => ({ ...m })),
    });
  },

  async moodJarSummary(): Promise<MoodJarSummary> {
    const period = new Date().toISOString().slice(0, 7);
    const inMonth = mockMoodJar.filter((m) => m.createdAt.slice(0, 7) === period);
    const days = new Set(inMonth.map((m) => m.createdAt.slice(0, 10)));
    return delay({ periodKey: period, entryCount: inMonth.length, activeDays: days.size });
  },

  async moodJarRecent(): Promise<MoodJarEntry[]> {
    return delay(mockMoodJar.slice(-40).reverse().map((m) => ({ ...m })));
  },

  async moodJarAdd(input: {
    moodId: string;
    moodOther?: string | null;
    intensity?: number;
    contextId?: string | null;
    contextOther?: string | null;
  }): Promise<MoodJarEntry> {
    const entry: MoodJarEntry = {
      id: uid(),
      moodId: input.moodId,
      moodOther:
        input.moodId === "other" && input.moodOther?.trim()
          ? input.moodOther.trim().slice(0, 20)
          : null,
      intensity: input.intensity ?? 3,
      contextId: input.contextId ?? null,
      contextOther:
        input.contextId === "other" && input.contextOther?.trim()
          ? input.contextOther.trim().slice(0, 25)
          : null,
      createdAt: new Date().toISOString(),
    };
    mockMoodJar.push(entry);
    return delay({ ...entry });
  },

  async moodJarRemove(id: string): Promise<void> {
    const i = mockMoodJar.findIndex((m) => m.id === id);
    if (i >= 0) mockMoodJar.splice(i, 1);
    return delay(undefined);
  },

  async moodJarDevSeed(input: {
    periodKey?: string;
    count?: number;
    activeDays?: number;
  }): Promise<MoodJarSummary> {
    const now = new Date();
    const prev = new Date(now.getFullYear(), now.getMonth() - 1, 1);
    const periodKey = input.periodKey ?? prev.toISOString().slice(0, 7);
    const count = input.count ?? 8;
    const activeDays = input.activeDays ?? 4;
    const [y, m] = periodKey.split("-").map(Number);
    for (let i = 0; i < count; i += 1) {
      const day = (i % activeDays) + 1;
      mockMoodJar.push({
        id: uid(),
        moodId: MOCK_MOOD_CATALOG[i % MOCK_MOOD_CATALOG.length].id,
        moodOther: null,
        intensity: (i % 5) + 1,
        contextId: MOCK_MOOD_CONTEXTS[i % (MOCK_MOOD_CONTEXTS.length - 1)].id,
        contextOther: null,
        createdAt: new Date(y, m - 1, day, 12).toISOString(),
      });
    }
    return delay({ periodKey, entryCount: count, activeDays });
  },

  // ---- Monthly Vibe Card (mock, in-memory) -------------------------------

  async monthlyVibeCardStatus(): Promise<MonthlyVibeCardStatusResponse> {
    const now = new Date();
    const current = now.toISOString().slice(0, 7);
    const prevDate = new Date(now.getFullYear(), now.getMonth() - 1, 1);
    const latestPeriod = prevDate.toISOString().slice(0, 7);
    const summary = await this.moodJarSummary();
    return delay({
      enabled: true,
      current: { periodKey: current, entryCount: summary.entryCount, activeDays: summary.activeDays },
      latest: mockMonthlyCardState(latestPeriod),
    });
  },

  async monthlyVibeCardGet(periodKey: string): Promise<MonthlyVibeCard> {
    return delay(mockMonthlyCardState(periodKey));
  },

  async monthlyVibeCardReveal(periodKey: string): Promise<MonthlyVibeCard> {
    if (mockMonthlyCards[periodKey]) {
      return delay({ ...mockMonthlyCards[periodKey] });
    }
    const id = uid();
    const card: MonthlyVibeCard = {
      periodKey,
      status: "available",
      cardId: id,
      createdAt: new Date().toISOString(),
      completedAt: new Date().toISOString(),
      content: {
        periodKey,
        headline: "Chief Optimist of Backup Plans",
        subline: "Every feeling arrived with its own unnecessary approval process.",
        tone: "dry",
        templateId: mockConcept(periodKey),
      },
      eligibility: null,
    };
    mockMonthlyCards[periodKey] = card;
    return delay({ ...card });
  },

  /** POST /users/me/deactivate — go quiet, keep everything. */
  async deactivateAccount(): Promise<{ deactivatedAt: string | null }> {
    const deactivatedAt = new Date().toISOString();
    if (me) me = { ...me, deactivatedAt };
    return delay({ deactivatedAt });
  },

  /** POST /users/me/reactivate. */
  async reactivateAccount(): Promise<{ deactivatedAt: string | null }> {
    if (me) me = { ...me, deactivatedAt: null };
    return delay({ deactivatedAt: null });
  },

  /** DELETE /users/me — wipe the mock account. */
  async deleteAccount(): Promise<{ ok: boolean }> {
    me = null;
    return delay({ ok: true });
  },

  /** DEV ONLY: force the plan so Plus-gated features can be tested. */
  async setDevPlan(plan: "free" | "plus"): Promise<User> {
    me = {
      ...(me ?? { id: "me", displayName: "", avatarSeed: uid() }),
      plan,
      // Mirrors what `presentUser` sends: the dev toggle buys a subscription,
      // so the mock has to say so or the profile row offers "Upgrade" to an
      // account the toggle just made Plus.
      plusSource: plan === "plus" ? "subscription" : null,
      supporterBadge: plan === "plus",
    };
    return delay({ ...me });
  },

  async getGuestProfile(userId: string): Promise<GuestProfile> {
    const nearbyUser = nearby.find((n) => n.id === userId);
    const chat = chats.find((c) => c.peerId === userId || c.userB === userId);

    // A block refuses ahead of every grant, exactly as the server does — and
    // with the same message every other refusal uses, so it cannot be read as
    // confirmation that a block exists.
    if (chat?.canUnblock) throw new Error("Guest profile not available");

    if (nearbyUser) {
      return delay(guestProfileFromNearby(nearbyUser, Boolean(chat?.revealed)));
    }

    if (chat) {
      return delay({
        id: chat.peerId,
        displayName: chat.peerName,
        venueTag: chat.peerVenueTag,
        avatarSeed: chat.peerAvatarSeed,
        ageBand: chat.peerAgeBand,
        headline: chat.peerHeadline,
        interests: chat.peerInterests,
        lookingFor: chat.peerLookingFor,
        languages: chat.peerLanguages,
        revealed: chat.revealed,
        revealDetails: chat.revealed
          ? mockRevealDetailsFromPeer({
              ageBand: chat.peerAgeBand,
              about: chat.peerAbout,
              interests: chat.peerInterests,
              gender: chat.peerGender,
              religion: chat.peerReligion,
              politics: chat.peerPolitics,
            })
          : {},
      });
    }

    return delay({
      id: userId,
      displayName: "Someone",
      avatarSeed: userId,
      revealed: false,
      revealDetails: {},
    });
  },

  // ---- Venue / presence ----
  async getCurrentVenue(): Promise<Venue | null> {
    return delay(venue);
  },

  async searchVenues(
    q: string,
    coords?: { lat: number; lng: number },
  ): Promise<
    {
      placeId: string;
      name: string;
      category?: string;
      address?: string;
      lat: number;
      lng: number;
    }[]
  > {
    const match = q.trim().toLowerCase();
    // Anchor the mock venues to the caller's live GPS (falling back to a fixed
    // point) so they land within the editor's 200m radius. Offsets are ~50-120m
    // — small enough everywhere on Earth, since a degree of longitude shrinks
    // toward the poles but the offsets are already well under the limit.
    const base = coords ?? { lat: 51.5074, lng: -0.1278 };
    const nearby = (
      dLat: number,
      dLng: number,
    ): { lat: number; lng: number } => ({
      lat: base.lat + dLat,
      lng: base.lng + dLng,
    });
    const all = [
      {
        placeId: "venue_brewhouse",
        name: "Brew House Café",
        category: "Café",
        address: "High St",
        ...nearby(0.0005, 0.0004),
      },
      {
        placeId: "venue_hogs",
        name: "Hogs Head Pub",
        category: "Bar",
        address: "Market Sq",
        ...nearby(-0.0007, 0.0006),
      },
      {
        placeId: "venue_hoglib",
        name: "Hogwarts Library",
        category: "Library",
        address: "Castle Rd",
        ...nearby(0.0003, -0.0008),
      },
    ];
    return delay(all.filter((v) => v.name.toLowerCase().includes(match)));
  },

  async joinVenue(candidate: {
    name: string;
    category?: string;
    gpsLat?: number;
    gpsLng?: number;
  }): Promise<Venue> {
    return delay({
      ...venue,
      name: candidate.name,
      category: candidate.category ?? venue.category,
    });
  },

  async getNearby(): Promise<NearbyResult> {
    // `total` is the length here because the mock set is smaller than any page
    // size. That is a property of the fixture, not of the contract — the two
    // fields are not interchangeable on the real API.
    // One page and no next one: the fixture is smaller than a real page, so
    // there is never anything after it to fetch.
    return delay({
      people: [...nearby],
      total: nearby.length,
      totalUnfiltered: null,
      nextCursor: null,
    });
  },

  // ---- Cues ----
  async sendCue(toUserId: string): Promise<Cue> {
    const target = nearby.find((n) => n.id === toUserId);
    const cue: Cue = {
      id: uid(),
      fromUserId: "me",
      toUserId,
      venueId: venue.id,
      status: "pending",
      createdAt: new Date().toISOString(),
      toName: target?.displayName,
      toAvatarSeed: target?.avatarSeed,
      toVenueTag: target?.venueTag,
      toAgeBand: target?.ageBand,
      toHeadline: target?.headline,
      toInterests: target?.interests,
      toLookingFor: target?.lookingFor,
    };
    cues.push(cue);
    return delay(cue);
  },

  async getInbox(): Promise<Cue[]> {
    return delay(
      cues.filter((c) => c.toUserId === "me" && c.status === "pending"),
    );
  },

  /**
   * The Sent ledger, mirroring the server: pending Cues plus the ones that were
   * accepted. Declines and expiries stay invisible here as they do there — a
   * mock that showed them would make the screen look like it sends rejection
   * notifications, which is the one thing it must never do.
   */
  async getSentCues(): Promise<Cue[]> {
    const mine = cues.filter(
      (c) =>
        c.fromUserId === "me" &&
        (c.status === "pending" || c.status === "accepted"),
    );
    return delay(
      mine.map((cue) =>
        cue.status === "accepted"
          ? {
              ...cue,
              chatId:
                chats.find(
                  (chat) =>
                    chat.userA === cue.toUserId || chat.userB === cue.toUserId,
                )?.id ?? null,
            }
          : cue,
      ),
    );
  },

  // ---- Business spots ----
  async claimSpot(candidate: {
    placeId: string;
    name: string;
    address?: string | null;
  }): Promise<OwnerSpot> {
    const spot: OwnerSpot = {
      id: uid(),
      slug: `${candidate.name.toLowerCase().replace(/[^a-z0-9]+/g, "-")}-${uid().slice(0, 6)}`,
      status: "pending",
      venueId: candidate.placeId,
      venueName: candidate.name,
      venueAddress: candidate.address ?? null,
      welcomeNote: null,
      meetupSpots: [],
      stats: {
        conversationsStarted: 0,
        groupsFormed: 0,
        meetupMoments: 0,
        becameVisible: 0,
      },
      createdAt: new Date().toISOString(),
    };
    mockSpots.unshift(spot);
    return delay(spot);
  },

  async getMySpots(): Promise<OwnerSpot[]> {
    return delay(mockSpots);
  },

  async getSpotDashboard(id: string): Promise<OwnerSpot> {
    const spot = mockSpots.find((s) => s.id === id);
    if (!spot) throw new Error("Spot not found");
    return delay(spot);
  },

  async updateSpot(
    id: string,
    patch: { welcomeNote?: string | null; meetupSpots?: string[] },
  ): Promise<OwnerSpot> {
    const spot = mockSpots.find((s) => s.id === id);
    if (!spot) throw new Error("Spot not found");
    if (patch.welcomeNote !== undefined)
      spot.welcomeNote = patch.welcomeNote?.trim() || null;
    if (patch.meetupSpots !== undefined) spot.meetupSpots = patch.meetupSpots;
    return delay(spot);
  },

  async getPublicSpot(slug: string): Promise<PublicSpot> {
    const spot = mockSpots.find((s) => s.slug === slug) ?? mockSpots[0];
    return delay({
      slug,
      venueName: spot?.venueName ?? "The Brew House",
      venueAddress: spot?.venueAddress ?? "12 Market Street",
      welcomeNote:
        spot?.welcomeNote ?? "Come in, grab a seat, and say hi to someone.",
      meetupSpots: spot?.meetupSpots ?? ["By the window", "Near the counter"],
      stats: {
        conversationsStarted: 18,
        groupsFormed: 5,
        meetupMoments: 3,
        becameVisible: 24,
      },
    });
  },

  async getPendingClaims(): Promise<AdminClaim[]> {
    return delay(
      mockSpots
        .filter((s) => s.status === "pending")
        .map((s) => ({
          id: s.id,
          slug: s.slug,
          status: s.status,
          venueName: s.venueName,
          venueAddress: s.venueAddress,
          ownerEmail: "owner@example.com",
          createdAt: s.createdAt,
        })),
    );
  },

  async setClaimStatus(
    id: string,
    status: "approved" | "rejected",
  ): Promise<OwnerSpot> {
    const spot = mockSpots.find((s) => s.id === id);
    if (!spot) throw new Error("Spot not found");
    spot.status = status;
    return delay(spot);
  },

  // ---- Growth & reflections ----
  async getProgressDashboard(
    days: 7 | 30 = 7,
    resetSince?: string | null,
  ): Promise<ProgressDashboard> {
    const end = new Date();
    const rangeStart = new Date(end);
    rangeStart.setDate(end.getDate() - days + 1);
    rangeStart.setHours(0, 0, 0, 0);
    const parsedReset = resetSince ? new Date(resetSince).getTime() : NaN;
    const since = Number.isFinite(parsedReset)
      ? new Date(Math.max(rangeStart.getTime(), parsedReset))
      : rangeStart;
    const inRange = (value: string | null | undefined) =>
      Boolean(value && new Date(value).getTime() >= since.getTime());
    const selectedReflections = mockReflections.filter((item) =>
      inRange(item.createdAt),
    );
    const countBy = <T extends string>(
      values: (T | null | undefined)[],
    ): { key: string; count: number }[] => {
      const counts = new Map<string, number>();
      values.forEach((value) => {
        if (value) counts.set(value, (counts.get(value) ?? 0) + 1);
      });
      return [...counts.entries()]
        .map(([key, count]) => ({ key, count }))
        .sort((a, b) => b.count - a.count);
    };
    const oldTrends = await this.getTrends(
      days === 30 ? "week" : "day",
      8,
      resetSince,
    );
    const completedGoals = mockGoals
      .filter((goal) => inRange(goal.completedAt))
      .sort(
        (a, b) =>
          new Date(b.completedAt ?? 0).getTime() -
          new Date(a.completedAt ?? 0).getTime(),
      );

    return delay({
      rangeDays: days,
      rangeStart: since.toISOString(),
      rangeEnd: end.toISOString(),
      practice: {
        completed: 0,
        activePractice: 0,
        skillsPractised: 0,
        mostPractised: null,
        categories: [],
        recent: [],
      },
      goals: {
        started: mockGoals.filter((goal) => inRange(goal.createdAt)).length,
        completed: completedGoals.length,
        archived: mockGoals.filter((goal) => inRange(goal.archivedAt)).length,
        removed: mockGoalEvents.filter(
          (event) => event.type === "removed" && inRange(event.at),
        ).length,
        activeNow: mockGoals.filter((goal) => !goal.archived && !goal.done)
          .length,
        // Mirrors the server's cap so mock mode exercises the same pager path.
        completedGoals: completedGoals.slice(0, 40).map((goal) => ({
          id: goal.id,
          title: goal.text,
          completedAt: goal.completedAt!,
        })),
        completedGoalsHasMore: completedGoals.length > 40,
      },
      reflections: {
        total: selectedReflections.length,
        insideOpncue: selectedReflections.filter(
          (item) => item.where === "opncue",
        ).length,
        outsideOpncue: selectedReflections.filter(
          (item) => item.where === "somewhere_else",
        ).length,
        bySubject: countBy(selectedReflections.map((item) => item.subject)),
        byFeeling: countBy(selectedReflections.map((item) => item.feeling)),
        byOutcome: countBy(selectedReflections.map((item) => item.outcome)),
        recent: selectedReflections.slice(0, 8).map((item) => ({
          id: item.id,
          where: item.where,
          subject: item.subject,
          feeling: item.feeling,
          outcome: item.outcome,
          createdAt: item.createdAt,
        })),
      },
      activity: {
        total: 9,
        cuesSent: 4,
        conversations: 3,
        groups: 1,
        meetups: 1,
        cuesReceived: 3,
        recent: [],
      },
      trends: {
        unit: days === 30 ? "week" : "day",
        points: oldTrends.map((point) => ({
          bucketStart: point.weekStart,
          practice: 0,
          goals: point.goalsCompleted,
          goalsStarted: 0,
          goalsCompleted: point.goalsCompleted,
          goalsArchived: 0,
          reflections: point.reflections,
          activity:
            point.cuesSent +
            point.conversationsStarted +
            point.groupsJoined +
            point.meetupMoments,
          cuesSent: point.cuesSent,
          conversations: point.conversationsStarted,
          groups: point.groupsJoined,
          meetups: point.meetupMoments,
        })),
      },
    });
  },

  async getGrowthSummary(
    days = 7,
    resetSince?: string | null,
  ): Promise<GrowthSummary> {
    const rangeSince = Date.now() - days * 24 * 60 * 60_000;
    const parsedReset = resetSince ? new Date(resetSince).getTime() : NaN;
    const since = Number.isFinite(parsedReset)
      ? Math.max(rangeSince, parsedReset)
      : rangeSince;
    const goalEventsInWindow = (type: "completed" | "removed") =>
      mockGoalEvents.filter(
        (e) => e.type === type && new Date(e.at).getTime() >= since,
      ).length;
    return delay({
      rangeDays: days,
      cuesSent: 4,
      cuesReceived: 3,
      conversationsStarted: 3,
      conversationsEnded: 2,
      groupsJoined: 1,
      meetupMoments: 1,
      reflections: mockReflections.length,
      goalsCompleted: goalEventsInWindow("completed"),
      goalsRemoved: goalEventsInWindow("removed"),
      goalsExpired: mockGoals.filter(isGoalExpired).length,
    });
  },

  async createReflection(input: ReflectionInput): Promise<Reflection> {
    const limits = reflectionLimits(me?.plan);
    if (limits.perMonth !== null && reflectionsUsedThisMonth() >= limits.perMonth) {
      throw new Error("reflection_quota_reached");
    }
    const reflection: Reflection = {
      id: uid(),
      where: input.where,
      subject: input.subject,
      feeling: input.feeling,
      outcome: input.outcome ?? null,
      note: input.note?.trim().slice(0, limits.noteMaxLength) || null,
      createdAt: new Date().toISOString(),
    };
    mockReflections.unshift(reflection);
    return delay(reflection);
  },

  async getReflections(limit = 10): Promise<Reflection[]> {
    return delay(mockReflections.slice(0, limit));
  },

  /**
   * Mirrors the server's filtered keyset paging, cursor included, so the
   * mock-backed build exercises the same load-more path as production rather
   * than a simplified one that would hide paging bugs.
   */
  async getReflectionHistory(
    query: ReflectionHistoryQuery = {},
  ): Promise<ReflectionPage> {
    const term = query.q?.trim().toLowerCase();
    const from = query.from ? new Date(query.from).getTime() : null;
    const to = query.to ? new Date(query.to).getTime() : null;

    let rows = mockReflections.filter((r) => {
      if (query.where && r.where !== query.where) return false;
      if (query.subject && r.subject !== query.subject) return false;
      if (query.feeling && r.feeling !== query.feeling) return false;
      if (query.outcome && r.outcome !== query.outcome) return false;
      const at = new Date(r.createdAt).getTime();
      if (from !== null && at < from) return false;
      if (to !== null && at > to) return false;
      if (term && !(r.note ?? "").toLowerCase().includes(term)) return false;
      return true;
    });

    // Newest first, id breaking ties — the server's sort key exactly.
    rows = [...rows].sort((a, b) => {
      const cmp = b.createdAt.localeCompare(a.createdAt);
      return cmp !== 0 ? cmp : b.id.localeCompare(a.id);
    });

    if (query.cursor) {
      const at = rows.findIndex((r) => r.id === decodeMockCursor(query.cursor));
      rows = at >= 0 ? rows.slice(at + 1) : rows;
    }

    const take = Math.min(Math.max(query.limit ?? 20, 1), 50);
    const items = rows.slice(0, take);
    const hasMore = rows.length > take;
    const last = items[items.length - 1];
    return delay({
      items,
      nextCursor: hasMore && last ? encodeMockCursor(last.id) : null,
    });
  },

  async deleteReflection(id: string): Promise<{ deleted: true }> {
    const at = mockReflections.findIndex((r) => r.id === id);
    if (at < 0) throw new Error("reflection_not_found");
    mockReflections.splice(at, 1);
    return delay({ deleted: true } as const);
  },

  async getReflectionQuota(): Promise<ReflectionQuota> {
    const plan = me?.plan ?? "free";
    const limits = reflectionLimits(plan);
    const used = reflectionsUsedThisMonth();
    const now = new Date();
    const resetsAt = new Date(now.getFullYear(), now.getMonth() + 1, 1);
    return delay({
      plan,
      used,
      usedToday: reflectionsUsedToday(),
      limit: limits.perMonth,
      remaining:
        limits.perMonth === null ? null : Math.max(0, limits.perMonth - used),
      resetsAt: resetsAt.toISOString(),
      noteMaxLength: limits.noteMaxLength,
      canExport: limits.canExport,
    });
  },

  async exportReflections(
    options: ReflectionExportOptions = {},
  ): Promise<ReflectionExport> {
    const plan = me?.plan ?? "free";
    if (plan !== "plus") {
      throw new Error("export_requires_plus");
    }

    // Mirrors the server's filtering rather than always returning everything:
    // a mock that ignores the options would hide the very behaviour the export
    // screen now depends on.
    const range = options.range ?? "all";
    const sections = new Set(
      options.sections ?? ["growthStats", "weeklySummaries", "reflections"],
    );
    const includeNotes = options.includeNotes ?? true;

    const since =
      range === "all"
        ? null
        : (() => {
            const start = new Date();
            start.setHours(0, 0, 0, 0);
            start.setDate(start.getDate() - (range === "last30" ? 29 : 364));
            return start;
          })();

    const reflections = sections.has("reflections")
      ? mockReflections
          .filter((item) => !since || new Date(item.createdAt) >= since)
          .map((item) => ({ ...item, note: includeNotes ? item.note : null }))
      : [];

    return delay({
      generatedAt: new Date().toISOString(),
      plan,
      range,
      totals: sections.has("growthStats")
        ? await this.getGrowthSummary(range === "last30" ? 30 : 365)
        : null,
      weeklySummaries: sections.has("weeklySummaries")
        ? await this.getTrends("week", range === "last30" ? 5 : 52)
        : [],
      reflections,
      reflectionsTruncated: false,
    });
  },

  async getTrends(
    unit: "day" | "week" = "week",
    buckets = 8,
    resetSince?: string | null,
  ): Promise<TrendPoint[]> {
    const span = unit === "week" ? 7 : 1; // days per bucket
    const n = Math.min(Math.max(buckets, 1), 52);
    const parsedReset = resetSince ? new Date(resetSince).getTime() : NaN;
    const points: TrendPoint[] = [];
    for (let i = n - 1; i >= 0; i -= 1) {
      // The last bucket (i = 0) ends today; earlier ones step back by `span`.
      const start = new Date();
      start.setHours(0, 0, 0, 0);
      start.setDate(start.getDate() - (i * span + (span - 1)));
      const end = new Date(start);
      end.setDate(end.getDate() + span);
      const effectiveStart =
        Number.isFinite(parsedReset) && parsedReset > start.getTime()
          ? parsedReset
          : start.getTime();
      const goalEventsInBucket = (type: "completed" | "removed") =>
        mockGoalEvents.filter((e) => {
          const at = new Date(e.at).getTime();
          return e.type === type && at >= effectiveStart && at < end.getTime();
        }).length;
      // Open goals whose due date falls in this bucket and has passed.
      const expiredInBucket = mockGoals.filter((g) => {
        if (!isGoalExpired(g) || !g.dueDate) return false;
        const at = new Date(`${g.dueDate}T23:59:59`).getTime();
        return at >= effectiveStart && at < end.getTime();
      }).length;
      // A gently rising, deterministic-ish shape so the chart looks alive.
      // Daily buckets hold a fraction of a week's activity, so scale them down.
      const base = n - i;
      const k = unit === "week" ? 1 : 0.35;
      const hiddenByReset = effectiveStart >= end.getTime();
      points.push({
        weekStart: start.toISOString(),
        reflections: hiddenByReset
          ? 0
          : Math.max(0, Math.round((base * 0.8 + (i % 2)) * k)),
        conversationsStarted: hiddenByReset
          ? 0
          : Math.max(0, Math.round(base * 0.6 * k)),
        groupsJoined: hiddenByReset
          ? 0
          : Math.max(0, Math.round(base * 0.25 * k)),
        meetupMoments: hiddenByReset
          ? 0
          : Math.max(0, Math.round(base * 0.25 * k)),
        cuesSent: hiddenByReset
          ? 0
          : Math.max(0, Math.round(base * 1.1 * k)),
        goalsCompleted: goalEventsInBucket("completed"),
        goalsRemoved: goalEventsInBucket("removed"),
        goalsExpired: expiredInBucket,
      });
    }
    return delay(points);
  },

  // ---- Social goals ----
  async getGoals(): Promise<SocialGoal[]> {
    return delay(
      [...mockGoals].sort(
        (a, b) =>
          Number(a.archived) - Number(b.archived) ||
          Number(a.done) - Number(b.done) ||
          new Date(b.archivedAt ?? b.createdAt).getTime() -
            new Date(a.archivedAt ?? a.createdAt).getTime(),
      ),
    );
  },

  async createGoal(
    text: string,
    dueDate: string | null = null,
    notes: string[] = [],
  ): Promise<SocialGoal> {
    if (activeMockGoals().length >= activeGoalLimit(me?.plan)) {
      throw new Error("goal_slots_full");
    }
    const goal: SocialGoal = {
      id: uid(),
      text: text.trim().slice(0, 120),
      done: false,
      archived: false,
      archivedAt: null,
      completedAt: null,
      createdAt: new Date().toISOString(),
      dueDate: dueDate || null,
      notes: cleanMockNotes(notes),
    };
    mockGoals.unshift(goal);
    return delay(goal);
  },

  async updateGoal(
    id: string,
    input: { text?: string; dueDate?: string | null; notes?: string[] },
  ): Promise<SocialGoal> {
    const goal = mockGoals.find((g) => g.id === id);
    if (!goal) throw new Error("Goal not found");
    if (goal.archived) throw new Error("Archived goals cannot be changed");
    if (input.text !== undefined) {
      const text = input.text.trim().slice(0, 120);
      if (!text) throw new Error("A goal needs some text");
      goal.text = text;
    }
    if (input.dueDate !== undefined) goal.dueDate = input.dueDate || null;
    if (input.notes !== undefined) goal.notes = cleanMockNotes(input.notes);
    return delay(goal);
  },

  async setGoalDone(id: string, done: boolean): Promise<SocialGoal> {
    const goal = mockGoals.find((g) => g.id === id);
    if (!goal) throw new Error("Goal not found");
    if (goal.archived) throw new Error("Archived goals cannot be changed");
    const wasDone = goal.done;
    goal.done = done;
    goal.completedAt = done ? new Date().toISOString() : null;
    // Record the completion once, on the transition to done, for the trends.
    if (done && !wasDone) {
      mockGoalEvents.push({ type: "completed", at: new Date().toISOString() });
    }
    return delay(goal);
  },

  async archiveGoal(id: string): Promise<SocialGoal> {
    const goal = mockGoals.find((g) => g.id === id);
    if (!goal) throw new Error("Goal not found");
    goal.archived = true;
    goal.archivedAt = goal.archivedAt ?? new Date().toISOString();
    return delay(goal);
  },

  async restoreGoal(id: string): Promise<SocialGoal> {
    const goal = mockGoals.find((g) => g.id === id);
    if (!goal) throw new Error("Goal not found");
    if (!goal.archived) return delay(goal);
    // Only an unfinished goal comes back into an active slot; see
    // GoalService.restore in the API.
    if (!goal.done && activeMockGoals().length >= activeGoalLimit(me?.plan)) {
      throw new Error("goal_slots_full");
    }
    goal.archived = false;
    goal.archivedAt = null;
    return delay(goal);
  },

  async removeGoal(id: string): Promise<{ ok: boolean }> {
    const idx = mockGoals.findIndex((g) => g.id === id);
    if (idx >= 0) mockGoals.splice(idx, 1);
    mockGoalEvents.push({ type: "removed", at: new Date().toISOString() });
    return delay({ ok: true });
  },

  async getCueCounts(): Promise<CueCounts> {
    const received = cues.filter((c) => c.toUserId === "me");
    const sent = cues.filter((c) => c.fromUserId === "me");
    // Mirrors the server: a slot is held while a cue is pending and stays spent
    // once accepted; everything else gives it back. Recipients count distinct
    // people, whatever became of the cue.
    const connectionsUsed = sent.filter(
      (c) => c.status === "pending" || c.status === "accepted",
    ).length;
    const recipientsUsed = new Set(sent.map((c) => c.toUserId)).size;
    const connectionsLimit = 5;
    const recipientsLimit = 10;
    return delay({
      connectionsUsed,
      connectionsLimit,
      connectionsAvailable: Math.max(0, connectionsLimit - connectionsUsed),
      recipientsUsed,
      recipientsLimit,
      recipientsAvailable: Math.max(0, recipientsLimit - recipientsUsed),
      pending: received.filter((c) => c.status === "pending").length,
    });
  },

  async cancelCue(cueId: string): Promise<{ status: "cancelled" }> {
    const cue = cues.find((c) => c.id === cueId);
    if (cue && cue.status === "pending") cue.status = "cancelled";
    return delay({ status: "cancelled" as const });
  },

  async respondToCue(
    cueId: string,
    status: "accepted" | "ignored" | "blocked",
  ): Promise<ChatSession | null> {
    const cue = cues.find((c) => c.id === cueId);
    if (!cue) return delay(null);
    cue.status = status;
    if (status !== "accepted") return delay(null);

    // One conversation per pair, like the real backend: `findOrCreate` hands
    // back the existing non-ended row and an accepted cue revives a dormant
    // one. The unconditional push this replaces created a second identical
    // session on every accept from the same peer — the peer-seeded display
    // name made the two rows pixel-identical, so the inbox looked like one
    // conversation "duplicating itself".
    const existing = chats.find(
      (c) =>
        (c.userA === cue.fromUserId || c.userB === cue.fromUserId) &&
        c.state !== "ended",
    );
    if (existing) {
      existing.state = "active";
      existing.active = true;
      existing.inactiveSince = null;
      existing.inactiveAt = chatInactiveAtFromNow();
      existing.reopenRequestedByMe = false;
      existing.reopenRequestedByPeer = false;
      existing.canRequestReopen = false;
      existing.updatedAt = new Date().toISOString();
      return delay(withChatLifecycleState(existing));
    }

    const peer = nearby.find((n) => n.id === cue.fromUserId);
    const chat: ChatSession = {
      id: uid(),
      userA: "me",
      userB: cue.fromUserId,
      ...publicPeerFields(
        peer ?? {
          id: cue.fromUserId,
          displayName: cue.fromName ?? "Someone",
          avatarSeed: cue.fromAvatarSeed ?? cue.fromUserId,
          venueTag: cue.fromVenueTag,
          status: "open",
          ageBand: cue.fromAgeBand,
          headline: cue.fromHeadline,
          interests: cue.fromInterests,
          lookingFor: cue.fromLookingFor,
        },
        cue.fromUserId,
      ),
      updatedAt: new Date().toISOString(),
      revealed: false,
      revealStatus: "idle",
      revealRequestedByMe: false,
      revealRequestedByPeer: false,
      active: true,
      state: "active",
      inactiveAt: chatInactiveAtFromNow(),
      inactiveWarning: false,
      inactiveSince: null,
      reopenRequestedByMe: false,
      reopenRequestedByPeer: false,
      canRequestReopen: false,
    };
    chats.push(chat);
    return delay(withChatLifecycleState(chat));
  },

  // ---- Chat ----
  /**
   * Sectioned like the real endpoint, so mock mode exercises the same client
   * paths. The mock set is small enough that nothing is ever capped, which is
   * why both cursors are always null and `totalCount` is just the length.
   */
  async getChats(): Promise<ChatInbox> {
    const sorted = [...chats]
      .map(withChatLifecycleState)
      .sort((a, b) => b.updatedAt.localeCompare(a.updatedAt));
    const inactive = sorted.filter((chat) => chat.state === "inactive");
    const live = sorted.filter((chat) => chat.state !== "inactive");
    return delay({
      live: { sessions: live, nextCursor: null, totalCount: live.length },
      inactive: {
        sessions: inactive,
        nextCursor: null,
        totalCount: inactive.length,
      },
    });
  },

  /**
   * The Open section, derived from the same rows as `getChats` — which is the
   * point of it being a mode of that query rather than its own list.
   *
   * Mirrors the server's exclusions so mock mode can actually exercise them: a
   * peer is open only if their lease is still in the future AND the
   * conversation is live. `withChatLifecycleState` decides the second half, so
   * the two agree here for the same reason they agree on the server.
   */
  async getOpenChats(): Promise<ChatSessionPage> {
    const open = [...chats]
      .map(withChatLifecycleState)
      .filter(
        (chat) =>
          chat.state !== "inactive" &&
          !!chat.peerOpenUntil &&
          Date.parse(chat.peerOpenUntil) > Date.now(),
      )
      .sort((a, b) => b.updatedAt.localeCompare(a.updatedAt));
    return delay({ sessions: open, nextCursor: null, totalCount: open.length });
  },

  /**
   * The toggle. `null` closes it, a duration opens a lease that far ahead.
   *
   * Computed here rather than echoed back, so mock mode shows the same thing
   * the server would: the lease the SERVER chose, not the duration asked for.
   */
  async setOpenToChat(durationMinutes: number | null) {
    const openUntil =
      durationMinutes === null
        ? null
        : new Date(Date.now() + durationMinutes * 60_000).toISOString();
    return delay({ openUntil });
  },

  /**
   * One conversation by id, mirroring `GET /chat/:id`.
   *
   * Reads from `chats` rather than from `nearby`: the peer fields were stored
   * on the row when it was created, so a mocked conversation keeps its
   * identity after the peer leaves the nearby list — which is the whole point
   * of the endpoint this stands in for.
   */
  async getChat(chatId: string): Promise<ChatSession> {
    const chat = chats.find((c) => c.id === chatId);
    if (!chat) throw new Error("Chat not found");
    return delay(withChatLifecycleState(chat));
  },


  /** The 1:1 twin of the group markRead. The mock keeps no read marks. */
  async markChatRead(_chatId: string): Promise<{ ok: boolean }> {
    return delay({ ok: true });
  },

  async unblockChat(chatId: string): Promise<ChatSession> {
    const chat = chats.find((c) => c.id === chatId);
    if (!chat) throw new Error("Chat not found");
    if (!chat.canUnblock) throw new Error("There is no block here to lift");
    // Becomes an ordinary dormant thread. Deliberately does NOT reopen: the way
    // back is "Ask to reopen" followed by the other person accepting, and on the
    // real API only the person who lifted the block is offered it.
    chat.canUnblock = false;
    chat.endedReason = null;
    return delay(withChatLifecycleState(chat));
  },

  async resumeChat(chatId: string): Promise<ChatSession> {
    const chat = chats.find((c) => c.id === chatId);
    if (!chat) throw new Error("Chat not found");
    if (!chat.canResume) throw new Error("You have not left this conversation");
    // Straight back to Active, alone. Nothing is requested, nothing is
    // accepted, and the mock deliberately touches no other field — the other
    // person's side was never changed to begin with.
    chat.canResume = false;
    chat.state = "active";
    chat.active = true;
    return delay(withChatLifecycleState(chat));
  },

  async requestReopen(chatId: string): Promise<ChatSession> {
    const chat = chats.find((c) => c.id === chatId);
    if (!chat) throw new Error("Chat not found");
    if (chat.state !== "inactive") throw new Error("Conversation is not inactive");
    if (chat.reopenRequestedByMe || chat.reopenRequestedByPeer) {
      throw new Error("A reopen request is already pending");
    }
    chat.reopenRequestedByMe = true;
    chat.canRequestReopen = false;
    return delay(withChatLifecycleState(chat));
  },

  async respondToReopen(chatId: string, accept: boolean): Promise<ChatSession> {
    const chat = chats.find((c) => c.id === chatId);
    if (!chat) throw new Error("Chat not found");
    if (!chat.reopenRequestedByPeer) throw new Error("No request to answer");

    chat.reopenRequestedByPeer = false;
    if (accept) {
      chat.state = "active";
      chat.active = true;
      chat.inactiveSince = null;
      chat.inactiveAt = chatInactiveAtFromNow();
      chat.canRequestReopen = false;
    } else {
      // The declined side is the peer here, so this viewer may still ask.
      chat.canRequestReopen = true;
    }
    return delay(withChatLifecycleState(chat));
  },

  async getMessages(
    chatId: string,
    after?: string,
    before?: string,
  ): Promise<ChatMessage[]> {
    const thread = messages.filter((m) => m.chatId === chatId);
    // Mirrors the real cursors so the catch-up loop terminates here too, and an
    // unknown id falls back to the whole thread rather than to nothing. The mock
    // set is small enough that it is always one page.
    if (after) {
      const from = thread.findIndex((m) => m.id === after);
      return delay(from >= 0 ? thread.slice(from + 1) : thread);
    }
    if (before) {
      const until = thread.findIndex((m) => m.id === before);
      return delay(until >= 0 ? thread.slice(0, until) : thread);
    }
    return delay(thread);
  },

  /**
   * Blocking removes the conversation. Exiting quietly no longer does: it goes
   * dormant, keeps its history, and only the person who left may ask for it
   * back — so `canRequestReopen` stays true for this viewer.
   */
  async endChat(
    chatId: string,
    outcome: "quiet" | "blocked",
  ): Promise<{ ok: boolean }> {
    const index = chats.findIndex((c) => c.id === chatId);
    if (index < 0) return delay({ ok: true });

    if (outcome === "blocked") {
      chats.splice(index, 1);
      return delay({ ok: true });
    }

    const chat = chats[index];
    chat.state = "inactive";
    chat.active = false;
    chat.inactiveSince = new Date().toISOString();
    chat.reopenRequestedByMe = false;
    chat.reopenRequestedByPeer = false;
    chat.canRequestReopen = true;
    chat.reopenCooldown = false;
    return delay({ ok: true });
  },

  async getPhotoExchange(chatId: string): Promise<PhotoExchangeState> {
    return delay(photoExchangeFor(chatId));
  },

  async requestPhotoExchange(chatId: string): Promise<PhotoExchangeState> {
    const state = photoExchangeFor(chatId);
    if (!state.canRequest) throw new Error("Cannot request right now");
    state.pending = {
      attemptId: uid(),
      byMe: true,
      expiresAt: new Date(Date.now() + 7 * 24 * 60 * 60 * 1000).toISOString(),
    };
    state.canRequest = false;
    return delay(state);
  },

  async acceptPhotoExchange(chatId: string): Promise<PhotoExchangeState> {
    const state = photoExchangeFor(chatId);
    if (!state.pending) throw new Error("Nothing to accept");
    state.pending = null;
    state.status = "revealed";
    state.canRequest = false;
    state.revealed = { myAssetId: uid(), peerAssetId: uid() };
    return delay(state);
  },

  async declinePhotoExchange(chatId: string): Promise<PhotoExchangeState> {
    return delay(closeMockAttempt(chatId));
  },

  async cancelPhotoExchange(chatId: string): Promise<PhotoExchangeState> {
    return delay(closeMockAttempt(chatId));
  },

  async deletePhoto(chatId: string, assetId: string): Promise<{ ok: boolean }> {
    const state = photoExchangeFor(chatId);
    if (state.revealed?.myAssetId === assetId) state.revealed.myAssetId = null;
    return delay({ ok: true });
  },

  /**
   * Wipe this viewer's copy. The conversation itself is untouched — it stays
   * in the list, still writable, just with nothing in it.
   */
  async clearMessages(chatId: string): Promise<ChatSession> {
    const chat = chats.find((c) => c.id === chatId);
    if (!chat) throw new Error("Chat not found");

    for (let i = messages.length - 1; i >= 0; i -= 1) {
      if (messages[i].chatId === chatId) messages.splice(i, 1);
    }
    chat.lastMessage = undefined;
    return delay(withChatLifecycleState(chat));
  },

  async getBlockedUsers(): Promise<BlockedUser[]> {
    return delay([...blockedUsers]);
  },

  async unblockUser(userId: string): Promise<{ ok: boolean }> {
    const index = blockedUsers.findIndex((u) => u.id === userId);
    // Mirrors the server, which refuses rather than silently succeeding: a lift
    // reporting success on a block still standing is the one outcome nobody can
    // detect from the outside.
    if (index === -1) throw new Error("There is no block here for you to lift");
    blockedUsers.splice(index, 1);
    return delay({ ok: true });
  },

  async reportUser(_input: ReportInput): Promise<{ ok: boolean }> {
    return delay({ ok: true });
  },


  async submitSupport(_input: SupportInput): Promise<SupportSubmitResult> {
    return delay({ id: uid(), status: "received" });
  },

  async sendMessage(
    chatId: string,
    content: string,
    replyToMessageId?: string,
  ): Promise<ChatMessage> {
    const replyTo = replyToMessageId
      ? messages.find((m) => m.chatId === chatId && m.id === replyToMessageId)
      : null;
    const msg: ChatMessage = {
      id: uid(),
      chatId,
      senderId: "me",
      content,
      timestamp: new Date().toISOString(),
      replyTo: replyTo
        ? {
            id: replyTo.id,
            senderId: replyTo.senderId,
            content: replyTo.content.slice(0, 240),
          }
        : null,
    };
    messages.push(msg);
    const chat = chats.find((c) => c.id === chatId);
    if (chat) {
      chat.lastMessage = content;
      chat.updatedAt = msg.timestamp;
      // Auto-reply so the chat feels alive in mock mode.
      setTimeout(() => {
        messages.push({
          id: uid(),
          chatId,
          senderId: chat.userB,
          content: pickReply(content),
          timestamp: new Date().toISOString(),
        });
      }, 1200);
    }
    return delay(msg);
  },

  async reactMessage(
    chatId: string,
    messageId: string,
    reaction: string | null,
  ): Promise<ChatMessage> {
    const message = messages.find(
      (m) => m.chatId === chatId && m.id === messageId,
    );
    if (!message) throw new Error("Message not found");
    message.reaction = reaction;
    return delay(message);
  },

  // ---- Small Groups ----
  async getGroups(): Promise<SmallGroup[]> {
    return delay(groups.map(refreshGroupTime));
  },

  async getGroupInbox(): Promise<SmallGroupInboxSections> {
    const rows = await delay(
      groups
        // Any membership this viewer has not deleted, exactly as the server
        // reads it now — `isMember` is false the moment they leave, and
        // filtering on it here is what made a left group vanish from the mock
        // inbox too. What each copy CONTAINS is stamped per message on the
        // server (`hidden_from`); there is no second sender in mock mode, so
        // there is nothing here to withhold.
        .filter(
          (group) =>
            group.viewerMembershipStatus &&
            group.viewerMembershipStatus !== "none" &&
            !hiddenGroupIds.has(group.id),
        )
        .map((group) => {
          const rows = groupMessages
            .filter((message) => message.groupId === group.id)
            .sort((a, b) => b.timestamp.localeCompare(a.timestamp));
          const last = rows[0] ?? null;
          return {
            ...refreshGroupTime(group),
            // Per VIEWER, like the server: a running group is inactive for
            // someone who is no longer in it.
            state:
              refreshGroupTime(group).minutesLeft > 0 &&
              group.viewerMembershipStatus === "active"
                ? ("active" as const)
                : ("inactive" as const),
            unreadCount: 0,
            lastMessage: last?.content ?? null,
            lastMessageAt: last?.timestamp ?? null,
            // Same rule as the server: latest visible activity, else when
            // the row appeared in this viewer's inbox. The mock keeps no join
            // clock, so a silent group falls back to "now" at first build —
            // never `expiresAt`, which is in the future and would squat the
            // row above every real message.
            updatedAt: last?.timestamp ?? new Date().toISOString(),
          };
        }),
    );
    // Sectioned like the server: active whole, inactive "paged" — the mock
    // holds so few rows that every page is the last one.
    const byRecency = (a: SmallGroupInbox, b: SmallGroupInbox) =>
      Date.parse(b.updatedAt) - Date.parse(a.updatedAt);
    const active = rows.filter((row) => row.state === "active").sort(byRecency);
    const inactive = rows.filter((row) => row.state !== "active").sort(byRecency);
    return {
      active,
      inactive: {
        groups: inactive,
        nextCursor: null,
        totalCount: inactive.length,
      },
    };
  },

  async deleteGroup(groupId: string): Promise<{ ok: boolean }> {
    // Per-member hide, like the server: the group row stays, my inbox forgets.
    hiddenGroupIds.add(groupId);
    return delay({ ok: true });
  },

  async createGroup(input: CreateSmallGroupInput): Promise<SmallGroup> {
    const group: SmallGroup = {
      id: uid(),
      name: input.name,
      vibe: input.vibe,
      // Derived from the floors, exactly as the server derives it — the mock
      // must not be the one place a label and a range can disagree.
      ageRange: ageBandRangeLabel({
        minFloor: input.ageBandMinFloor ?? AGE_BAND_MIN_FLOOR,
        maxFloor: input.ageBandMaxFloor ?? OPEN_AGE_BAND_FLOOR,
      }),
      prompt: input.prompt || "Anyone want to sit together for a bit?",
      maxMembers: input.maxMembers,
      radiusM: input.radiusM,
      memberCount: 1,
      minutesLeft: input.durationMinutes,
      expiresAt: new Date(Date.now() + input.durationMinutes * 60_000).toISOString(),
      isMember: true,
      hostUserId: "me",
      viewerMembershipStatus: "active",
      members: [
        {
          id: "me",
          displayName: me?.displayName || "You",
          avatarSeed: me?.avatarSeed ?? "me",
          avatarConfig: me?.avatarConfig ?? null,
          ageBand: me?.ageBand ?? null,
          readyToMeet: false,
          isHost: true,
        },
      ],
    };
    groups.unshift(group);
    // No seeded system message — matches the real API: a new group opens on
    // the empty-room placeholder, not a permanent "created" banner.
    return delay(refreshGroupTime(group));
  },

  async getGroup(groupId: string): Promise<SmallGroup> {
    const group = groups.find((item) => item.id === groupId);
    if (!group) throw new Error("Small Group not found");
    return delay(refreshGroupTime(group));
  },

  /**
   * The invite preview. Mock mode has no real position, so nobody is ever out
   * of range here — the out-of-range path is exercised by the API's own specs,
   * and a mock that guessed at distance would only teach the UI a fiction.
   */
  async getGroupInvite(groupId: string): Promise<SmallGroupInvite> {
    const group = groups.find((item) => item.id === groupId);
    if (!group) throw new Error("Small Group not found");
    const isMember = group.members.some((member) => member.id === "me");
    return delay({
      id: group.id,
      name: group.name,
      vibe: group.vibe,
      ageRange: group.ageRange,
      prompt: group.prompt,
      maxMembers: group.maxMembers,
      memberCount: group.memberCount,
      radiusM: group.radiusM,
      minutesLeft: group.minutesLeft,
      expiresAt: group.expiresAt,
      isMember,
      block: group.memberCount >= group.maxMembers && !isMember ? "full" : null,
    });
  },

  async setGroupRadius(groupId: string, radiusM: number): Promise<SmallGroup> {
    const group = groups.find((item) => item.id === groupId);
    if (!group) throw new Error("Small Group not found");
    group.radiusM = radiusM;
    return delay(refreshGroupTime(group));
  },

  async joinGroup(groupId: string): Promise<SmallGroup> {
    const group = groups.find((item) => item.id === groupId);
    if (!group) throw new Error("Small Group not found");
    if (!group.members.some((member) => member.id === "me")) {
      group.members.push({
        id: "me",
        displayName: me?.displayName || "You",
        avatarSeed: me?.avatarSeed ?? "me",
        avatarConfig: me?.avatarConfig ?? null,
        ageBand: me?.ageBand ?? null,
        readyToMeet: false,
        isHost: group.hostUserId === "me",
      });
    }
    group.isMember = true;
    group.viewerMembershipStatus = "active";
    group.memberCount = group.members.length;
    groupMessages.push({
      id: uid(),
      groupId,
      senderId: null,
      senderName: null,
      kind: "system",
      content: `${me?.displayName || "You"} joined the group.`,
      timestamp: new Date().toISOString(),
    });
    return delay(refreshGroupTime(group));
  },

  async leaveGroup(groupId: string): Promise<{ ok: boolean }> {
    const group = groups.find((item) => item.id === groupId);
    if (group) {
      group.members = group.members.filter((member) => member.id !== "me");
      group.memberCount = group.members.length;
      group.isMember = false;
      group.viewerMembershipStatus = "left";
      groupMessages.push({
        id: uid(),
        groupId,
        senderId: null,
        senderName: null,
        kind: "system",
        content: "You left the group.",
        timestamp: new Date().toISOString(),
      });
    }
    return delay({ ok: true });
  },

  async getGroupMessages(
    groupId: string,
    after?: string,
    before?: string,
    limit?: number,
  ): Promise<SmallGroupMessage[]> {
    const thread = groupMessages.filter(
      (message) => message.groupId === groupId,
    );
    const page = limit ?? thread.length;

    // Walking BACK. Without this the mock answered a `before` request with the
    // newest page, so scrolling up in mock mode asked for history and was
    // handed the present — and `hasOlder` stayed true, so it asked again.
    if (before) {
      const at = thread.findIndex((m) => m.id === before);
      const earlier = at >= 0 ? thread.slice(0, at) : [];
      return delay(earlier.slice(Math.max(0, earlier.length - page)));
    }

    // Mirrors the real cursor so the catch-up loop terminates here too: an
    // unknown id falls back to the newest page rather than to nothing.
    const from = after ? thread.findIndex((m) => m.id === after) : -1;
    const rows = from >= 0 ? thread.slice(from + 1) : thread;
    // The newest page, like the server: the tail, not the head.
    return delay(from >= 0 ? rows.slice(0, page) : rows.slice(-page));
  },

  async sendGroupMessage(
    groupId: string,
    content: string,
  ): Promise<SmallGroupMessage> {
    const message: SmallGroupMessage = {
      id: uid(),
      groupId,
      senderId: "me",
      senderName: me?.displayName || "You",
      kind: "user",
      content,
      timestamp: new Date().toISOString(),
    };
    groupMessages.push(message);
    return delay(message);
  },

  async suggestGroupMeetup(
    groupId: string,
    spot: string,
    customSpot?: string,
  ): Promise<SmallGroupMessage> {
    const message: SmallGroupMessage = {
      id: uid(),
      groupId,
      senderId: null,
      senderName: null,
      kind: "system",
      content: /[?.!]$/.test(customSpot || spot)
        ? `${me?.displayName || "You"} suggested: ${customSpot || spot}`
        : `${me?.displayName || "You"} suggested meeting ${
            spot === "Custom" ? customSpot || "nearby" : spot.toLowerCase()
          }.`,
      timestamp: new Date().toISOString(),
    };
    groupMessages.push(message);
    return delay(message);
  },

  async removeGroupMember(
    groupId: string,
    memberId: string,
  ): Promise<{
    status: "removed";
    groupId: string;
    memberId: string;
    removedAt: string;
  }> {
    const group = groups.find((item) => item.id === groupId);
    if (!group) throw new Error("Small Group not found");
    group.members = group.members.filter((member) => member.id !== memberId);
    group.memberCount = group.members.length;
    const removedAt = new Date().toISOString();
    return delay({ status: "removed", groupId, memberId, removedAt });
  },

  async readyGroup(groupId: string): Promise<SmallGroupMessage[]> {
    const group = groups.find((item) => item.id === groupId);
    const member = group?.members.find((item) => item.id === "me");
    if (member) member.readyToMeet = true;
    const message: SmallGroupMessage = {
      id: uid(),
      groupId,
      senderId: null,
      senderName: null,
      kind: "system",
      content: `${me?.displayName || "You"} is ready to say hi.`,
      timestamp: new Date().toISOString(),
    };
    groupMessages.push(message);
    return delay([message]);
  },

  // ---- Reveal ----
  async requestReveal(
    chatId: string,
  ): Promise<{ status: "pending_peer" | "revealed"; result?: RevealResult }> {
    const state = (reveals[chatId] ??= { a: false, b: false });
    state.a = true;
    // Mock peer agrees after a short beat ~half the time.
    state.b = true;
    if (state.a && state.b) {
      const chat = chats.find((c) => c.id === chatId);
      if (chat) {
        chat.revealed = true;
        chat.revealStatus = "revealed";
        chat.revealRequestedByMe = true;
        chat.revealRequestedByPeer = true;
      }
      return delay({
        status: "revealed" as const,
        result: {
          firstName: chat?.peerName.split(" ")[0] ?? "Sam",
          proximityHint:
            proximityHints[Math.floor(Math.random() * proximityHints.length)],
          sharedDetails: {
            age: 28,
            about: "Enjoys relaxed conversation, coffee, and finding good local spots.",
            gender: "non-binary",
            religion: "spiritual",
            politics: "not-political",
          },
        },
      });
    }
    const chat = chats.find((c) => c.id === chatId);
    if (chat) {
      chat.revealStatus = "requested";
      chat.revealRequestedByMe = true;
      chat.revealRequestedByPeer = false;
    }
    return delay({ status: "pending_peer" as const });
  },

  // ---- Conversation Practice (private AI roleplay; never touches real chats) ----

  practiceScenarios(): Promise<PracticeScenario[]> {
    return delay(PRACTICE_SCENARIOS);
  },

  practiceCredits(): Promise<PracticeCredits> {
    return delay(practiceCreditsState());
  },

  practicePacks(): Promise<PracticePack[]> {
    return delay(PRACTICE_PACKS);
  },

  practiceStartSession(
    scenarioId: string,
    sceneSource: "curated" | "byo" = "curated",
  ): Promise<PracticeSessionView> {
    const scenario =
      PRACTICE_SCENARIOS.find((s) => s.id === scenarioId) ?? PRACTICE_SCENARIOS[0];
    for (const found of practiceSessions.values()) {
      if (
        found.session.scenarioId === scenario.id &&
        found.session.status === "active"
      ) {
        return delay(found);
      }
    }
    const byo = sceneSource === "byo";
    const session: PracticeSession = {
      id: `ps_${Date.now()}`,
      scenarioId: scenario.id,
      status: "active",
      practiceMode: "guided",
      sceneSource,
      byoReady: false,
      startedAt: new Date().toISOString(),
      endedAt: null,
    };
    // BYO starts with an empty transcript + an empty intake log (the client shows
    // the recount prompt); curated starts with the opener line.
    if (byo) {
      const view: PracticeSessionView = { session, messages: [], pack: null, intake: [] };
      practiceSessions.set(session.id, view);
      return delay(view);
    }
    const opening: PracticeMessage = {
      id: `pm_${Date.now()}`,
      role: "coach",
      content: "Hey — I don't think we've met. First time here?",
      createdAt: new Date().toISOString(),
    };
    practiceSessions.set(session.id, { session, messages: [opening], pack: null });
    return delay({ session, messages: [opening], pack: null });
  },

  practiceUseOwnScene(id: string): Promise<PracticeSession> {
    const found = practiceSessions.get(id);
    if (!found) throw new Error("Practice session not found.");
    found.session = { ...found.session, sceneSource: "byo", byoReady: false };
    found.messages = [];
    found.pack = null;
    found.intake = [];
    return delay(found.session);
  },

  practiceIntakeTurn(id: string, content: string): Promise<ByoIntakeResult> {
    const found = practiceSessions.get(id);
    if (!found) throw new Error("Practice session not found.");
    found.intake = found.intake ?? [];
    // Every intake call spends a reply, like the roleplay.
    if (practiceCreditsState().remainingReplies <= 0) {
      throw new Error("out_of_practice_replies");
    }
    found.intake.push({ role: "user", content });
    // Simple mock: capture a scene on the first recount and hand off.
    const say = "Got it — let's run it back. Say your first line whenever you're ready.";
    found.intake.push({ role: "guide", content: say });
    found.session = { ...found.session, byoReady: true };
    if (practiceCreditsState().includedRemaining > 0) practiceIncludedUsed += 1;
    else practicePurchasedRemaining = Math.max(0, practicePurchasedRemaining - 1);
    return delay({
      status: "ready",
      say,
      ready: true,
      session: found.session,
      credits: practiceCreditsState(),
    });
  },

  practiceActiveSessions(): Promise<PracticeSession[]> {
    const sessions = Array.from(practiceSessions.values())
      .map((entry) => entry.session)
      .filter((session) => session.status === "active")
      .sort((a, b) => b.startedAt.localeCompare(a.startedAt));
    return delay(sessions);
  },

  practiceGetSession(id: string): Promise<PracticeSessionView> {
    const found = practiceSessions.get(id);
    if (!found) throw new Error("Practice session not found.");
    return delay(found);
  },

  practiceSendMessage(id: string, content: string): Promise<PracticeSendResult> {
    const found = practiceSessions.get(id);
    if (!found) throw new Error("Practice session not found.");
    const before = practiceCreditsState();
    if (before.remainingReplies <= 0) throw new Error("out_of_practice_replies");

    found.messages.push({
      id: `pm_${Date.now()}_u`,
      role: "user",
      content,
      createdAt: new Date().toISOString(),
    });
    const coach: PracticeMessage = {
      id: `pm_${Date.now()}_c`,
      role: "coach",
      content: pickPracticeReply(content),
      createdAt: new Date().toISOString(),
    };
    found.messages.push(coach);
    if (before.includedRemaining > 0) {
      practiceIncludedUsed += 1;
    } else {
      practicePurchasedRemaining = Math.max(0, practicePurchasedRemaining - 1);
    }

    const credits = practiceCreditsState();
    return delay({ message: coach, credits, warning: credits.warning });
  },

  practiceSummarize(id: string): Promise<PracticeSummaryResponse> {
    const found = practiceSessions.get(id);
    if (!found) throw new Error("Practice session not found.");
    const userMessages = found.messages.filter(
      (message) => message.role === "user" && message.content.trim().length > 12,
    );
    const coachMessages = found.messages.filter(
      (message) => message.role === "coach" && message.content.trim(),
    );
    if (userMessages.length === 0) {
      return delay({
        status: "not_enough_practice",
        reason: "no_practice",
        session: found.session,
      });
    }
    if (userMessages.length < 2 || coachMessages.length < 2) {
      return delay({
        status: "not_enough_practice",
        reason: "too_short",
        session: found.session,
      });
    }
    const summary: PracticeSummary = {
      version: 2,
      mainTakeaway:
        "You kept the practice moving; the next step is giving the other person a little more to respond to.",
      didWell: [
        "You answered in a way that left room for the conversation to continue.",
      ],
      tryNext: {
        guidance:
          "Add one small detail about yourself before asking the next question.",
        example: "I come here when I need somewhere calm to reset. How about you?",
        reason: "That makes the exchange feel more balanced and less like an interview.",
      },
      realLifeStep:
        "The next time you are waiting somewhere, make one simple comment about the shared situation and let that be enough.",
    };
    found.session = {
      ...found.session,
      status: "ended",
      endedAt: new Date().toISOString(),
      summaryStatus: "READY",
      summaryVersion: 2,
      summaryData: summary,
      summaryGeneratedAt: new Date().toISOString(),
    };
    return delay({ status: "ready", session: found.session, summary });
  },

  practiceEnd(id: string): Promise<PracticeSession> {
    const found = practiceSessions.get(id);
    if (!found) throw new Error("Practice session not found.");
    found.session = { ...found.session, status: "ended", endedAt: new Date().toISOString() };
    return delay(found.session);
  },

  practiceClearSession(id: string): Promise<{ ok: boolean }> {
    practiceSessions.delete(id);
    return delay({ ok: true });
  },

  practicePurchase(pack: string): Promise<{ clientSecret: string }> {
    // Mock: instantly grant the pack's replies instead of a real checkout.
    const found = PRACTICE_PACKS.find((p) => p.id === pack);
    if (found) practicePurchasedRemaining += found.replies;
    return delay({ clientSecret: "" });
  },
};

// ---- Practice mock state ----

const PRACTICE_SCENARIOS: PracticeScenario[] = [
  {
    id: "sc_break_ice",
    slug: "break_ice",
    title: "Break the Ice",
    description: "Practice opening naturally when there is a small shared moment.",
    aiRole: "Friendly stranger in a low-pressure public setting.",
    userGoal: "Open naturally from a small shared moment.",
  },
  {
    id: "sc_keep_going",
    slug: "keep_going",
    title: "Keep It Going",
    description: "Practice turning a reply into a real back-and-forth.",
    aiRole: "Someone who is friendly but not carrying the whole conversation.",
    userGoal: "Turn a reply into a balanced back-and-forth.",
  },
  {
    id: "sc_repair_moment",
    slug: "repair_moment",
    title: "Repair a Moment",
    description: "Practice clearing up awkward wording, missed cues, small tension, or misunderstandings.",
    aiRole: "Someone in a low-pressure public moment where a small repair would help.",
    userGoal: "Acknowledge what happened, repair it briefly, and leave room.",
  },
  {
    id: "sc_set_boundary",
    slug: "set_boundary",
    title: "Set a Boundary",
    description: "Practice saying no, slowing things down, or ending kindly.",
    aiRole: "Someone who wants more time or attention than the user wants to give.",
    userGoal: "Say no, slow things down, or end kindly and clearly.",
  },
  {
    id: "sc_make_plans_follow_up",
    slug: "make_plans_follow_up",
    title: "Make Plans & Follow Up",
    description: "Practice suggesting a low-pressure next step, or sending a friendly follow-up after meeting.",
    aiRole: "Someone the user recently met.",
    userGoal: "Suggest a low-pressure next step or send a friendly follow-up.",
  },
  {
    id: "sc_confidence",
    slug: "confidence",
    title: "Build Confidence",
    description: "Practice one small social move when you are hesitant or unsure.",
    aiRole: "Supportive coach for low-pressure social confidence.",
    userGoal: "Try one small social move while feeling hesitant or unsure.",
  },
];

const PRACTICE_PACKS: PracticePack[] = [
  {
    id: "pack_100",
    replies: 100,
    price: 2.5,
    label: "100 practice replies",
    storeProductIds: { ios: "100_opncue_pack", android: "100_opncue_pack" },
  },
  {
    id: "pack_250",
    replies: 250,
    price: 4.5,
    label: "250 practice replies",
    storeProductIds: { ios: "opncue_pack_250", android: "opncue_pack_250" },
  },
  {
    id: "pack_550",
    replies: 550,
    price: 8.5,
    label: "550 practice replies",
    storeProductIds: { ios: "opncue_pack_550", android: "opncue_pack_550" },
  },
];

const practiceSessions = new Map<string, PracticeSessionView>();
let practiceIncludedUsed = 0;
let practicePurchasedRemaining = 0;

function practiceIncludedAllowance(): number {
  return me?.plan === "plus" ? 100 : 0;
}

function practiceCreditsState(): PracticeCredits {
  const includedRemaining = Math.max(
    0,
    practiceIncludedAllowance() - practiceIncludedUsed,
  );
  const remainingReplies = includedRemaining + practicePurchasedRemaining;
  const warning =
    remainingReplies <= 0
      ? "empty"
      : remainingReplies <= 2
        ? "two_left"
        : remainingReplies <= 5
          ? "five_left"
          : "none";
  const now = new Date();
  const start = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), 1));
  const end = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth() + 1, 1));
  return {
    includedRemaining,
    purchasedRemaining: practicePurchasedRemaining,
    remainingReplies,
    billingPeriodStart: start.toISOString(),
    billingPeriodEnd: end.toISOString(),
    warning,
  };
}

function pickPracticeReply(input: string): string {
  const replies = [
    "That's a warm, easy opener. I'd smile and say, \"Yeah, I come here a fair bit — do you?\"",
    "Nice — that feels natural. A small follow-up question keeps it going.",
    "Good one. Keep it light and let them share a little.",
    "That works. Remember: you don't have to fill every pause.",
  ];
  return replies[Math.min(replies.length - 1, Math.floor(input.length / 10))];
}

function pickReply(input: string): string {
  const replies = [
    "Haha, totally. What brought you here today?",
    "Nice — first time at this spot?",
    "Same! The coffee here is underrated.",
    "Oh interesting, tell me more 👀",
    "I was just thinking that too.",
  ];
  if (input.toLowerCase().includes("?"))
    return replies[Math.floor(Math.random() * replies.length)];
  return replies[Math.floor(Math.random() * replies.length)];
}
